import { useEffect, useRef } from "react";

import { useVidyoStore } from "@/features/vidyo-call/utils/vidyo-store";

import { useStartClosedCaptioning } from "./start-closed-captioning-token";
import { useStopClosedCaptioning } from "./stop-closed-captioning";

export function useSubscribeToClosedCaptioning() {
  const vidyoSDK = useVidyoStore((state) => state.vidyoSDK);
  const startClosedCaptioning = useStartClosedCaptioning();
  const stopClosedCaptioning = useStopClosedCaptioning({ enabled: false });
  const setSubscriptionToken = useVidyoStore(
    (state) => state.closedCaptioning.setSubscriptionToken,
  );
  const setMessage = useVidyoStore(
    (state) => state.closedCaptioning.setMessage,
  );
  const subscriptionToken = useVidyoStore(
    (state) => state.closedCaptioning.subscriptionToken,
  );
  const enabled = useVidyoStore((state) => state.closedCaptioning.enabled);

  // saving the subscription token, because we need it to unsubscribe
  useEffect(() => {
    if (startClosedCaptioning.data) {
      setSubscriptionToken(startClosedCaptioning.data.data.subscriptionToken);
    }
  }, [startClosedCaptioning.data, setSubscriptionToken]);

  /**
   * Previous enabled state
   */
  const prevEnabledRef = useRef<boolean>(false);

  useEffect(() => {
    const handleSubscribe = async () => {
      if (
        !vidyoSDK ||
        !subscriptionToken ||
        !enabled ||
        prevEnabledRef.current
      ) {
        return;
      }

      try {
        await vidyoSDK.SubscribeToTopic({
          topicSubscriptionToken: subscriptionToken,
          messageCallback: setMessage,
          statusCallback: (status, message, code) => {
            console.log(
              `[useSubscribeToClosedCaptioning] ${status} ${message} ${code}`,
            );
          },
        });
        prevEnabledRef.current = enabled;
      } catch (error) {
        console.error(
          "[useSubscribeToClosedCaptioning] Error subscribing to closed captioning: ",
          error,
        );
      }
    };

    handleSubscribe();
  }, [
    enabled,
    vidyoSDK,
    startClosedCaptioning.data,
    startClosedCaptioning.isSuccess,
    subscriptionToken,
    setMessage,
  ]);

  // Unsubscribe when enabled becomes false (and was previously true)
  useEffect(() => {
    const handleUnsubscribe = async () => {
      if (
        !vidyoSDK ||
        !subscriptionToken ||
        enabled ||
        !prevEnabledRef.current
      ) {
        return;
      }

      console.log(
        "[useSubscribeToClosedCaptioning] Unsubscribing from closed captioning",
      );
      try {
        await vidyoSDK.UnsubscribeFromTopic({
          topicSubscriptionToken: subscriptionToken,
        });
        await stopClosedCaptioning.refetch();
        prevEnabledRef.current = false;
      } catch (error) {
        console.error(
          "[useSubscribeToClosedCaptioning] Error unsubscribing from closed captioning: ",
          error,
        );
      }
    };

    if (!enabled && prevEnabledRef.current) {
      handleUnsubscribe();
    }

    return () => {
      if (!enabled && prevEnabledRef.current) {
        handleUnsubscribe();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
}
